import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { UserDetails } from '../types/User';

interface UseDashboardViewProps {
  userDetails: UserDetails | null;
  userReady?: boolean;
  onLogout: () => void;
}

/**
 * Custom hook to manage DocumentManagement view state and sidebar configuration
 * Extracts view switching, sidebar setup, and reminder handling logic
 */
export const useDashboardView = ({
  userDetails,
  userReady,
  onLogout
}: UseDashboardViewProps) => {
  const navigate = useNavigate();
  const { activeItem, setItems, setFooterItems, setActiveItem } = useSidebar();
  const [currentView, setCurrentView] = useState('agent_dashboard');

  const agencyDashboardSidebarItems = useMemo(() => {
    const isManagerOrAdmin = userDetails?.role === 'Manager' || userDetails?.role === 'Admin';
    const baseItems = [
      { id: 'agent_dashboard', label: 'Agent', icon: '👤' },
      ...(isManagerOrAdmin ? [{ id: 'agency_dashboard', label: 'Agency', icon: '🏢' }] : []),
      ...(isManagerOrAdmin ? [{ id: 'company_dashboard', label: 'Company', icon: '🏢' }] : []),
      { id: 'agent_reports', label: 'Agent Reports', icon: '📋' },
      ...(isManagerOrAdmin ? [{ id: 'agency_reports', label: 'Agency Reports', icon: '🏢' }] : [])
    ];
    return baseItems;
  }, [userDetails?.role]);

  const accountSidebarFooterItems = useMemo(() => [
    { id: 'help', label: 'Help', to: '/ContactUs', icon: '⁉️' },
    { id: 'privacy', label: 'Privacy Policy', to: '/PrivacyPolicy', icon: '⛓️‍💥' },
    { id: 'logout', label: 'Logout', onClick: () => { onLogout() }, icon: '💤' }
  ], [onLogout]);


  // Initialize sidebar on mount — useLayoutEffect prevents flash of stale items
  useLayoutEffect(() => {
    setItems(agencyDashboardSidebarItems);
    setFooterItems(accountSidebarFooterItems);
    setActiveItem('agent_dashboard');
  }, [agencyDashboardSidebarItems, accountSidebarFooterItems, setItems, setFooterItems, setActiveItem]);

  // Sync view with active sidebar item
  useEffect(() => {
    const knownViews = ['agent_dashboard', 'agency_dashboard', 'company_dashboard', 'agent_reports', 'agency_reports'];
    if (activeItem && knownViews.includes(activeItem)) {
      setCurrentView(activeItem);
    } else {
      setCurrentView('agent_dashboard');
    }
  }, [activeItem]);

  // Validation and navigation logic
  useEffect(() => {
    // Redirect to login if no user
    if (!userDetails && userReady !== false) {
      navigate('/LoginRegister', { replace: true });
      return;
    }

    // Redirect to Account if no agency selected
    if (userReady && userDetails && !userDetails.agency) {
      alert('Please select an active agency before managing documents.');
      navigate('/Account');
    }
  }, [userReady, userDetails, navigate]);

  return {
    currentView
  };
};
